import { useState, useEffect } from "react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plane, Calendar, User, Trash2, Plus, AlertCircle } from "lucide-react";
import { useAttendance } from "@/hooks/useAttendance";
import { useAuth } from "@/hooks/useAuth";
import { employeeService } from "@/services/employeeService";
import { Employee } from "@/types/employee";
import { AbsenceType } from "@/types/attendance";
import dayjs from "dayjs";

export const AbsenceManager = () => {
  const { company } = useAuth();
  const slug = company?.slug || "";
  const company_id = company?.id || "";
  
  const { absences, isLoadingAbsences, createAbsence, deleteAbsence } = useAttendance(slug);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Form State
  const [employeeId, setEmployeeId] = useState("");
  const [type, setType] = useState<AbsenceType>("Vacation");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (slug) {
      employeeService.getEmployees(slug).then((res) => {
        setEmployees(res.data.data || []);
      });
    }
  }, [slug]);

  const handleSubmit = async () => {
    if (!employeeId || !startDate || !endDate) return;

    await createAbsence.mutateAsync({
      employee_id: employeeId,
      type,
      start_date: startDate,
      end_date: endDate,
      description,
      slug,
      company_id
    });

    setIsDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setEmployeeId("");
    setType("Vacation");
    setStartDate("");
    setEndDate("");
    setDescription("");
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'Vacation': return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-none">Vacaciones</Badge>;
      case 'Medical': return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-none">Médico</Badge>;
      case 'Permission': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-none">Permiso</Badge>;
      default: return <Badge variant="outline">{type}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Plane className="h-5 w-5 text-indigo-600" />
            Gestión de Ausencias y Vacaciones
          </h2>
          <p className="text-sm text-slate-500">
            Registra y administra los periodos de descanso de tu personal.
          </p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
              <Plus className="h-4 w-4" />
              Registrar Ausencia
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Nueva Ausencia / Vacación</DialogTitle>
              <DialogDescription>
                Completa los datos para registrar el periodo de ausencia. El sistema bloqueará asignaciones de turnos en estas fechas.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium">Empleado</label>
                <Select value={employeeId} onValueChange={setEmployeeId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona un empleado" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.identification})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium">Tipo de Ausencia</label>
                <Select value={type} onValueChange={(v: AbsenceType) => setType(v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Vacation">Vacaciones</SelectItem>
                    <SelectItem value="Medical">Permiso Médico</SelectItem>
                    <SelectItem value="Permission">Permiso Personal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Fecha Inicio</label>
                  <Input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)} 
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Fecha Fin</label>
                  <Input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)} 
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium">Descripción / Motivo</label>
                <Textarea 
                  placeholder="Opcional: Detalles adicionales..." 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={!employeeId || !startDate || !endDate || createAbsence.isPending}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {createAbsence.isPending ? "Guardando..." : "Guardar Registro"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Empleado</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Desde</TableHead>
              <TableHead>Hasta</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingAbsences ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  Cargando ausencias...
                </TableCell>
              </TableRow>
            ) : absences?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  <div className="flex flex-col items-center gap-2">
                    <Calendar className="h-8 w-8 text-slate-300" />
                    <p>No hay ausencias registradas actualmente.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              absences?.map((absence: any) => (
                <TableRow key={absence.id} className="hover:bg-slate-50/50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-xs">
                        {absence.employee?.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{absence.employee?.name}</p>
                        <p className="text-xs text-slate-500">ID: {absence.employee?.identification}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getTypeBadge(absence.type)}</TableCell>
                  <TableCell>{dayjs(absence.start_date).format('DD/MM/YYYY')}</TableCell>
                  <TableCell>{dayjs(absence.end_date).format('DD/MM/YYYY')}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-slate-600 italic">
                    {absence.description || "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-slate-400 hover:text-red-600"
                      onClick={() => deleteAbsence.mutate(absence.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
